import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthProvider';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { PerfilUsuario, UsuarioMaestro } from '../models/scope';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';
import { LABEL_AREA, LABEL_PERFIL_USUARIO } from '../utils/labels';

interface ModalGerenciarUsuariosMaestroProps {
  aberto: boolean;
  onFechar: () => void;
}

interface Membro {
  membroId: number;
  usuarioId: number;
  nome: string;
  email: string;
  perfil: PerfilUsuario;
  areaId?: number;
  ativo: boolean;
}

const PERFIS: PerfilUsuario[] = [
  'ADMINISTRADOR_PLATAFORMA', 'TI', 'DIRETORIA', 'CONTROLLER', 'GERENCIA_CONTABIL',
  'COORDENACAO_CONTABIL', 'ANALISTA_CONTABIL', 'PREPARADOR', 'REVISOR', 'APROVADOR',
  'RESPONSAVEL_AREA', 'AUDITORIA', 'COMPLIANCE', 'VISUALIZADOR', 'RESPONSAVEL_SOX',
];

// Vínculo real de usuários ao Maestro (MaestroUsuarioMembro) — resolve a etapa de setup
// PERFIS_NIVEIS/RESPONSAVEIS deixar de ser só uma atestação em texto: só quem for adicionado aqui
// passa a aparecer nos seletores de responsável/preparador/revisor/aprovador do módulo inteiro.
const ModalGerenciarUsuariosMaestro: React.FC<ModalGerenciarUsuariosMaestroProps> = ({ aberto, onFechar }) => {
  const { usuario } = useAuth();
  const { areas, recarregar } = useMaestroContext();
  const [membros, setMembros] = useState<Membro[]>([]);
  const [usuariosPlataforma, setUsuariosPlataforma] = useState<UsuarioMaestro[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [usuarioIdNovo, setUsuarioIdNovo] = useState(0);
  const [perfilNovo, setPerfilNovo] = useState<PerfilUsuario>('VISUALIZADOR');
  const [areaIdNovo, setAreaIdNovo] = useState(0);
  const [enviando, setEnviando] = useState(false);

  const carregar = () => {
    setCarregando(true);
    setErro(null);
    Promise.all([
      maestroService.listarMembrosMaestro(),
      usuario?.clienteID ? maestroService.listarUsuariosPlataforma(usuario.clienteID) : Promise.resolve([]),
    ])
      .then(([listaMembros, listaPlataforma]) => { setMembros(listaMembros); setUsuariosPlataforma(listaPlataforma); })
      .catch((e: any) => setErro(e?.message ?? 'Não foi possível carregar os usuários do Maestro.'))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { if (aberto) carregar(); }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps

  const idsJaMembros = new Set(membros.filter(m => m.ativo).map(m => m.usuarioId));
  const disponiveisParaAdicionar = usuariosPlataforma.filter(u => !idsJaMembros.has(u.id));

  const adicionar = () => {
    if (!usuarioIdNovo) return;
    setEnviando(true);
    maestroService.adicionarMembroMaestro(usuarioIdNovo, perfilNovo, areaIdNovo || undefined)
      .then(() => { setUsuarioIdNovo(0); setPerfilNovo('VISUALIZADOR'); setAreaIdNovo(0); carregar(); recarregar(); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível adicionar o usuário.'))
      .finally(() => setEnviando(false));
  };

  const atualizarPerfil = (membro: Membro, perfil: PerfilUsuario) =>
    maestroService.atualizarMembroMaestro(membro.membroId, perfil, membro.areaId).then(() => { carregar(); recarregar(); }).catch(() => {});

  const atualizarArea = (membro: Membro, areaId: number) =>
    maestroService.atualizarMembroMaestro(membro.membroId, membro.perfil, areaId || undefined).then(() => { carregar(); recarregar(); }).catch(() => {});

  const remover = (membro: Membro) => {
    if (!window.confirm(`Remover ${membro.nome} do Maestro Contábil?`)) return;
    maestroService.removerMembroMaestro(membro.membroId).then(() => { carregar(); recarregar(); }).catch(() => dispararAcaoSimulada('Não foi possível remover o usuário.'));
  };

  const reativar = (membro: Membro) =>
    maestroService.adicionarMembroMaestro(membro.usuarioId, membro.perfil, membro.areaId).then(() => { carregar(); recarregar(); }).catch(() => {});

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth='md' fullWidth PaperProps={{ className: 'mc-dialog' }}>
      <DialogTitle>Usuários do Maestro Contábil</DialogTitle>
      <DialogContent>
        <p className='mc-texto-neutro' style={{ marginTop: 0 }}>Só usuários adicionados aqui aparecem nos seletores de responsável, preparador, revisor e aprovador do módulo.</p>

        {erro && <p className='mc-texto-danger'>{erro}</p>}

        <div className='mc-form-inline'>
          <label>Usuário
            <select value={usuarioIdNovo} onChange={e => setUsuarioIdNovo(Number(e.target.value))}>
              <option value={0}>Selecione...</option>
              {disponiveisParaAdicionar.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </label>
          <label>Perfil no Maestro
            <select value={perfilNovo} onChange={e => setPerfilNovo(e.target.value as PerfilUsuario)}>
              {PERFIS.map(p => <option key={p} value={p}>{LABEL_PERFIL_USUARIO[p]}</option>)}
            </select>
          </label>
          <label>Área (opcional)
            <select value={areaIdNovo} onChange={e => setAreaIdNovo(Number(e.target.value))}>
              <option value={0}>Sem área específica</option>
              {areas.map(a => <option key={a.id} value={a.id}>{LABEL_AREA[a.tipo]}</option>)}
            </select>
          </label>
          <button type='button' className='mc-btn mc-btn--primary' disabled={enviando || !usuarioIdNovo} onClick={adicionar}>
            {enviando ? 'Adicionando...' : 'Adicionar'}
          </button>
        </div>

        {carregando
          ? <p className='mc-texto-neutro'>Carregando...</p>
          : membros.length === 0
            ? <p className='mc-texto-neutro'>Nenhum usuário vinculado ao Maestro ainda.</p>
            : (
              <div className='mc-membros-lista'>
                {membros.map(m => (
                  <div key={m.membroId} className='mc-membro-card' style={{ opacity: m.ativo ? 1 : .5 }}>
                    <div className='mc-membro-card__usuario'>
                      <span className='mc-membro-card__avatar'>{m.nome.trim().charAt(0)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div className='mc-membro-card__nome'>{m.nome}</div>
                        <div className='mc-membro-card__email'>{m.email}</div>
                      </div>
                    </div>
                    <select value={m.perfil} disabled={!m.ativo} onChange={e => atualizarPerfil(m, e.target.value as PerfilUsuario)}>
                      {PERFIS.map(p => <option key={p} value={p}>{LABEL_PERFIL_USUARIO[p]}</option>)}
                    </select>
                    <select value={m.areaId ?? 0} disabled={!m.ativo} onChange={e => atualizarArea(m, Number(e.target.value))}>
                      <option value={0}>Sem área específica</option>
                      {areas.map(a => <option key={a.id} value={a.id}>{LABEL_AREA[a.tipo]}</option>)}
                    </select>
                    <span className={`mc-badge mc-badge--${m.ativo ? 'success' : 'neutro'}`}>{m.ativo ? 'Ativo' : 'Removido'}</span>
                    {m.ativo
                      ? <button type='button' className='mc-btn mc-btn--sm mc-btn--secondary' onClick={() => remover(m)}>Remover</button>
                      : <button type='button' className='mc-btn mc-btn--sm mc-btn--secondary' onClick={() => reativar(m)}>Reativar</button>}
                  </div>
                ))}
              </div>
            )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalGerenciarUsuariosMaestro;
